import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link, useSearchParams, redirect } from 'react-router'
import { useState, useCallback, useTransition } from 'react'
import { db } from '@afficheservices/db'
import { serviceCategories, professionalProfiles, professionalCategories, reviews } from '@afficheservices/db/schema'
import { eq, sql, gte, and, isNull, ne } from 'drizzle-orm'
import { formatRating } from '~/lib/format'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { ProMap } from '~/components/pro-map'
import { FAQSchema, ServiceSchema, BreadcrumbSchema, StructuredData } from '~/components/structured-data'
import { InstantQuote } from '~/components/instant-quote'
import { InlineQuiz } from '~/components/inline-quiz'
import { ArticleSchema, HowToSchema, ComparisonTable } from '~/components/seo-content'
import { AnswerBlock } from '~/components/answer-block'
import { TOP_CITIES, artisanSlug, EMERGENCY_SERVICES, SERVICE_LABELS } from '~/lib/programmatic-seo'
import { CATEGORY_SEO } from '~/lib/category-seo'
import {
  Star, CheckCircle, Clock, ArrowRight, MapPin, Shield, Users,
  SlidersHorizontal, X, ChevronLeft, ChevronRight, Loader2,
  Wrench, Zap, Paintbrush, HardHat, Hammer, Home, Key,
  SprayCan, TreePine, Snowflake, Truck, Scissors,
} from 'lucide-react'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'

export function headers() { return SEO_PAGE_HEADERS }

// ─── Constants ───────────────────────────────────────────────

const PAGE_SIZE = 15

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  plomberie: Wrench, electricite: Zap, peinture: Paintbrush, renovation: HardHat,
  menuiserie: Hammer, toiture: Home, serrurerie: Key, nettoyage: SprayCan,
  jardinage: TreePine, climatisation: Snowflake, demenagement: Truck, 'carrelage-sol': Scissors,
}

const RATING_OPTIONS = [
  { value: '', label: 'Toutes' },
  { value: '4', label: '4+' },
  { value: '4.5', label: '4.5+' },
]

const SORT_OPTIONS = [
  { value: 'rating', label: 'Meilleure note' },
  { value: 'reviews', label: 'Plus d\'avis' },
  { value: 'response_time', label: 'Réponse rapide' },
  { value: 'experience', label: 'Expérience' },
]

const EXPERIENCE_OPTIONS = [
  { value: '', label: 'Toute' },
  { value: '2', label: '2+ ans' },
  { value: '5', label: '5+ ans' },
  { value: '10', label: '10+ ans' },
]

// ─── Loader ──────────────────────────────────────────────────

const SLUG_ALIASES: Record<string, string> = {
  carrelage: 'carrelage-sol',
  carreleur: 'carrelage-sol',
  zinguerie: 'couverture-zinguerie',
  couvreur: 'couverture-zinguerie',
  solaire: 'panneaux-solaires',
  solaires: 'panneaux-solaires',
  photovoltaique: 'panneaux-solaires',
  clim: 'climatisation',
  platre: 'platrerie',
  platrier: 'platrerie',
  serrure: 'serrurerie',
  serrurier: 'serrurerie',
  electricien: 'electricite',
  plombier: 'plomberie',
  macon: 'maconnerie',
  peintre: 'peinture',
  jardinier: 'jardinage',
  menuisier: 'menuiserie',
  demenagement: 'demenagement',
  demenageur: 'demenagement',
  piscine: 'piscine',
  piscines: 'piscine',
  isolation: 'isolation',
  toiture: 'toiture',
  renovation: 'renovation',
  securite: 'securite',
}

export async function loader({ params, request }: LoaderFunctionArgs) {
  const { category: slug } = params
  const url = new URL(request.url)

  if (slug && SLUG_ALIASES[slug] && SLUG_ALIASES[slug] !== slug) {
    throw redirect(`/services/${SLUG_ALIASES[slug]}${url.search}`, 301)
  }

  const ratingFilter = url.searchParams.get('rating') ?? ''
  const cityFilter = url.searchParams.get('city') ?? ''
  const sortFilter = url.searchParams.get('sort') ?? 'rating'
  const experienceFilter = url.searchParams.get('experience') ?? ''
  const verifiedOnly = url.searchParams.get('verified') === '1'
  const page = Math.max(1, parseInt(url.searchParams.get('page') ?? '1', 10))

  const [cat] = await db.select().from(serviceCategories)
    .where(eq(serviceCategories.slug, slug ?? '')).limit(1)

  if (!cat) throw new Response('Catégorie non trouvée', { status: 404 })

  // Get subcategories
  const subs = await db
    .select({ id: serviceCategories.id, name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories)
    .where(eq(serviceCategories.parentId, cat.id))

  // Build conditions
  const conditions = [eq(professionalCategories.categoryId, cat.id)]

  if (ratingFilter) {
    // ratingAvg is decimal(3,2) — must compare against the decimal column
    // explicitly cast, otherwise drizzle's string-vs-decimal coercion can
    // silently mis-filter (4 vs "4" can return wrong rows).
    const ratingNum = parseFloat(ratingFilter)
    if (!Number.isNaN(ratingNum)) {
      conditions.push(sql`${professionalProfiles.ratingAvg}::numeric >= ${ratingNum}`)
    }
  }
  if (cityFilter) {
    conditions.push(eq(professionalProfiles.city, cityFilter))
  }
  if (experienceFilter) {
    conditions.push(gte(professionalProfiles.yearsExperience, parseInt(experienceFilter, 10)))
  }
  if (verifiedOnly) {
    conditions.push(eq(professionalProfiles.identityVerified, true))
  }

  // Count
  const [{ count: totalCount }] = await db
    .select({ count: sql<number>`count(*)::int` })
    .from(professionalProfiles)
    .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
    .where(and(...conditions))

  // Sort
  let orderBy
  switch (sortFilter) {
    case 'reviews':
      orderBy = sql`${professionalProfiles.reviewCount} DESC NULLS LAST`
      break
    case 'response_time':
      orderBy = sql`${professionalProfiles.responseTimeAvgHours} ASC NULLS LAST`
      break
    case 'experience':
      orderBy = sql`${professionalProfiles.yearsExperience} DESC NULLS LAST`
      break
    default:
      orderBy = sql`${professionalProfiles.ratingAvg} DESC NULLS LAST`
      break
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const offset = (Math.min(page, totalPages) - 1) * PAGE_SIZE

  // Promoted pros (featured or enterprise tier) — always show first
  const promotedPros = await db
    .select({
      id: professionalProfiles.id,
      companyName: professionalProfiles.companyName,
      slug: professionalProfiles.slug,
      city: professionalProfiles.city,
      departmentCode: professionalProfiles.departmentCode,
      ratingAvg: professionalProfiles.ratingAvg,
      reviewCount: professionalProfiles.reviewCount,
      responseTimeAvgHours: professionalProfiles.responseTimeAvgHours,
      identityVerified: professionalProfiles.identityVerified,
      insuranceVerified: professionalProfiles.insuranceVerified,
      shortDescription: professionalProfiles.shortDescription,
      yearsExperience: professionalProfiles.yearsExperience,
      employeeCount: professionalProfiles.employeeCount,
      latitude: professionalProfiles.latitude,
      longitude: professionalProfiles.longitude,
      isPromoted: sql<boolean>`true`,
    })
    .from(professionalProfiles)
    .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
    .where(
      and(
        eq(professionalCategories.categoryId, cat.id),
        eq(professionalProfiles.identityVerified, true),
        sql`(${professionalProfiles.isFeatured} = true OR ${professionalProfiles.subscriptionTier} = 'enterprise')`,
      ),
    )
    .orderBy(sql`${professionalProfiles.ratingAvg} DESC NULLS LAST`)
    .limit(2)

  const _promotedIds = new Set(promotedPros.map((p) => p.id))

  const pros = await db
    .select({
      id: professionalProfiles.id,
      companyName: professionalProfiles.companyName,
      slug: professionalProfiles.slug,
      city: professionalProfiles.city,
      departmentCode: professionalProfiles.departmentCode,
      ratingAvg: professionalProfiles.ratingAvg,
      reviewCount: professionalProfiles.reviewCount,
      responseTimeAvgHours: professionalProfiles.responseTimeAvgHours,
      identityVerified: professionalProfiles.identityVerified,
      insuranceVerified: professionalProfiles.insuranceVerified,
      shortDescription: professionalProfiles.shortDescription,
      yearsExperience: professionalProfiles.yearsExperience,
      employeeCount: professionalProfiles.employeeCount,
      latitude: professionalProfiles.latitude,
      longitude: professionalProfiles.longitude,
    })
    .from(professionalProfiles)
    .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
    .where(and(...conditions))
    .orderBy(orderBy)
    .limit(PAGE_SIZE)
    .offset(offset)

  // Distinct cities for filter
  const citiesResult = await db
    .selectDistinct({ city: professionalProfiles.city })
    .from(professionalProfiles)
    .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
    .where(eq(professionalCategories.categoryId, cat.id))
    .orderBy(professionalProfiles.city)

  const availableCities = citiesResult.map((c) => c.city).filter(Boolean)

  // Category-specific FAQs for rich snippets
  const CATEGORY_FAQS: Record<string, Array<{ question: string; answer: string }>> = {
    plomberie: [
      { question: 'Combien coûte un plombier ?', answer: 'Le tarif moyen d\'un plombier est de 80 à 500 € selon le type d\'intervention. Un dépannage simple coûte 80-200 €, une installation complète 300-1200 €.' },
      { question: 'Comment choisir un bon plombier ?', answer: 'Vérifiez les avis clients, l\'assurance RC Pro et le SIRET. Sur AfficheServices, tous les plombiers sont vérifiés et notés par de vrais clients.' },
      { question: 'Un plombier peut-il intervenir en urgence ?', answer: 'Oui, de nombreux plombiers proposent des urgences 24h/24. Les interventions nuit/week-end coûtent 50 à 100% plus cher.' },
      { question: 'Le devis est-il obligatoire ?', answer: 'Oui, un devis écrit est obligatoire pour tout travail dépassant 150 €. Sur AfficheServices, vous recevez jusqu\'à 3 devis gratuits.' },
      { question: 'Quelles garanties pour les travaux de plomberie ?', answer: 'Le plombier doit fournir une garantie de parfait achèvement (1 an) et être couvert par une assurance RC Professionnelle.' },
    ],
    electricite: [
      { question: 'Combien coûte un électricien ?', answer: 'Les tarifs varient de 150 à 1500 €. Installation de prises : 80-150 €/point, mise aux normes : 1000-3000 €, rénovation complète : 3000-8000 €.' },
      { question: 'La mise aux normes électrique est-elle obligatoire ?', answer: 'Oui, la norme NF C 15-100 est obligatoire pour les constructions neuves et rénovations importantes. Un diagnostic est requis pour la vente.' },
      { question: 'Quelles certifications pour un électricien ?', answer: 'Vérifiez la certification Qualifelec ou RGE. L\'attestation CONSUEL est obligatoire après travaux.' },
      { question: 'Peut-on bénéficier d\'aides pour les travaux électriques ?', answer: 'Oui, MaPrimeRénov\' et les CEE sont disponibles pour les travaux d\'amélioration énergétique réalisés par un artisan RGE.' },
    ],
    peinture: [
      { question: 'Quel est le prix de la peinture au m² ?', answer: 'Comptez 20-45 €/m² pour les murs intérieurs, 25-50 €/m² pour le plafond, 30-60 €/m² pour la façade extérieure, pose comprise.' },
      { question: 'Combien de temps pour peindre un appartement ?', answer: '3 à 5 jours pour un T3 (70m²) incluant préparation des surfaces, 2 couches et nettoyage.' },
      { question: 'Quelle peinture choisir pour l\'intérieur ?', answer: 'La peinture acrylique (à l\'eau) est recommandée : peu d\'odeur, séchage rapide. Pour les pièces humides, optez pour une peinture anti-humidité.' },
    ],
    renovation: [
      { question: 'Combien coûte une rénovation complète ?', answer: 'Rafraîchissement : 200-400 €/m², rénovation moyenne : 400-800 €/m², rénovation lourde : 800-1500 €/m². Un T3 de 70m² coûte entre 14000 et 56000 €.' },
      { question: 'Quelles aides pour la rénovation ?', answer: 'MaPrimeRénov\', éco-PTZ, CEE et TVA réduite à 5.5% pour les travaux d\'amélioration énergétique.' },
      { question: 'Par où commencer une rénovation ?', answer: 'Diagnostic (structure, électricité, plomberie), puis : gros œuvre → isolation → plomberie/électricité → revêtements → finitions.' },
    ],
  }

  const defaultFaqs = [
    { question: `Comment trouver un bon professionnel en ${cat.nameFr.toLowerCase()} ?`, answer: `Sur AfficheServices, tous les professionnels en ${cat.nameFr.toLowerCase()} sont vérifiés : SIRET valide, assurance RC Pro, identité contrôlée. Comparez les avis et les devis gratuitement.` },
    { question: 'Le service AfficheServices est-il gratuit ?', answer: 'Oui, AfficheServices est 100% gratuit pour les particuliers. Recevez jusqu\'à 3 devis sans engagement.' },
    { question: 'En combien de temps reçoit-on les devis ?', answer: 'Vous recevez généralement vos devis sous 48 heures. Les professionnels les mieux notés répondent souvent en quelques heures.' },
    { question: 'Les artisans sont-ils assurés ?', answer: 'Oui, nous vérifions l\'assurance RC Professionnelle de chaque artisan avant son inscription sur la plateforme.' },
  ]

  const faqs = CATEGORY_FAQS[slug ?? ''] ?? defaultFaqs

  // Aggregate rating for this category (for rich snippets)
  const [aggRating] = await db
    .select({
      avgRating: sql<string>`ROUND(AVG(${reviews.overallRating})::numeric, 1)`,
      totalReviews: sql<number>`count(*)::int`,
    })
    .from(reviews)
    .innerJoin(professionalProfiles, eq(professionalProfiles.id, reviews.professionalId))
    .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
    .where(and(
      eq(professionalCategories.categoryId, cat.id),
      eq(reviews.status, 'approved'),
    ))

  // Related categories for internal linking
  const relatedCategories = await db
    .select({ name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories)
    .where(and(
      isNull(serviceCategories.parentId),
      ne(serviceCategories.slug, slug ?? ''),
    ))
    // Stable ordering: a sibling-category list that reshuffles per request
    // means a different internal link graph on every crawl.
    .orderBy(serviceCategories.displayOrder)
    .limit(6)

  return {
    category: cat,
    subcategories: subs,
    pros,
    cities: availableCities,
    filters: { rating: ratingFilter, city: cityFilter, sort: sortFilter, experience: experienceFilter, verified: verifiedOnly },
    pagination: { page, totalPages, total: totalCount },
    faqs,
    aggregateRating: aggRating ?? null,
    relatedCategories,
    promotedPros: promotedPros.filter((p) => !pros.some((pp) => pp.id === p.id)),
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const name = data?.category?.nameFr ?? 'Service'
  const slug = data?.category?.slug ?? ''
  // Hand-authored per-category meta — falls back to generic when category
  // not in CATEGORY_SEO map yet.
  const seo = CATEGORY_SEO[slug]
  const titleRaw = seo?.title ?? `${name} — Trouvez un pro | AfficheServices`
  const descRaw = seo?.description ?? `Comparez les devis de ${data?.pagination?.total ?? 0} professionnels en ${name.toLowerCase()}. Artisans vérifiés, devis gratuits sous 48h.`
  // SERP truncation safety net: cap title at ~60 chars, desc at ~155 chars
  // so Google doesn't crop key CTAs. Hand-authored CATEGORY_SEO entries
  // already aim для these limits, but fallback strings + occasional long
  // entries can overflow.
  const title = titleRaw.length > 60 ? titleRaw.slice(0, 57).trim() + '…' : titleRaw
  const desc = descRaw.length > 155 ? descRaw.slice(0, 152).trim() + '…' : descRaw
  const url = `https://www.afficheservices.fr/services/${slug}`
  return [
    { title },
    { name: 'description', content: desc },
    { tagName: 'link', rel: 'canonical', href: url },
    { property: 'og:title', content: `${name} — Trouvez un pro` },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: url },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'AfficheServices' },
    { property: 'og:image', content: 'https://www.afficheservices.fr/og-default.png' },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:image', content: 'https://www.afficheservices.fr/og-default.png' },
  ]
}

// ─── Component ───────────────────────────────────────────────

export default function CategoryPage() {
  const { category, subcategories, pros, cities, filters, pagination, faqs, aggregateRating, relatedCategories, promotedPros } = useLoaderData<typeof loader>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [isPending, startTransition] = useTransition()

  const Icon = ICON_MAP[category.slug] ?? Wrench

  const activeFilterCount = [
    filters.rating,
    filters.city,
    filters.experience,
    filters.verified,
  ].filter(Boolean).length

  const updateFilter = useCallback(
    (key: string, value: string) => {
      startTransition(() => {
        const params = new URLSearchParams(searchParams)
        if (value) {
          params.set(key, value)
        } else {
          params.delete(key)
        }
        params.delete('page')
        setSearchParams(params, { replace: true })
      })
    },
    [searchParams, setSearchParams],
  )

  const clearAllFilters = useCallback(() => {
    startTransition(() => {
      setSearchParams(new URLSearchParams(), { replace: true })
    })
  }, [setSearchParams])

  const goToPage = useCallback(
    (page: number) => {
      startTransition(() => {
        const params = new URLSearchParams(searchParams)
        if (page > 1) {
          params.set('page', String(page))
        } else {
          params.delete('page')
        }
        setSearchParams(params, { replace: true })
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [searchParams, setSearchParams],
  )

  // ── Filter controls ────────────────────────────────────────

  function FilterControls({ className = '' }: { className?: string }) {
    return (
      <div className={className}>
        {/* Rating */}
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Note minimum</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {RATING_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateFilter('rating', opt.value === filters.rating ? '' : opt.value)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${
                  opt.value === filters.rating
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {opt.value && <Star className="mr-1 inline h-3 w-3 fill-amber-400 text-amber-400" />}
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* City */}
        {cities.length > 0 && (
          <div className="mt-5">
            <label htmlFor="cat-filter-city" className="text-xs font-semibold uppercase tracking-wider text-slate-400">Ville</label>
            <select
              id="cat-filter-city"
              value={filters.city}
              onChange={(e) => updateFilter('city', e.target.value)}
              className="mt-2 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Toutes les villes</option>
              {cities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
        )}

        {/* Experience */}
        <div className="mt-5">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Expérience</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {EXPERIENCE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateFilter('experience', opt.value === filters.experience ? '' : opt.value)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${
                  opt.value === filters.experience
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Verified only */}
        <div className="mt-5">
          <button
            type="button"
            onClick={() => updateFilter('verified', filters.verified ? '' : '1')}
            className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-sm font-medium transition-all ${
              filters.verified
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <Shield className="h-4 w-4" />
            Vérifié uniquement
          </button>
        </div>

        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="mt-5 w-full rounded-lg border border-border px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50"
          >
            Effacer les filtres ({activeFilterCount})
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />

      {/* Structured Data for SEO. Merge hand-authored category FAQs (top-funnel
          intent) with auto-generated faqs (bottom-funnel pricing) to give
          Google + AI extractors a richer FAQPage entity. */}
      <ServiceSchema category={category} />
      <FAQSchema items={[...(CATEGORY_SEO[category.slug]?.faqs.map(f => ({ question: f.q, answer: f.a })) ?? []), ...faqs]} />
      <ArticleSchema
        title={`${category.nameFr} — Artisans vérifiés | AfficheServices`}
        description={`Trouvez un ${category.nameFr.toLowerCase()} de confiance. Artisans vérifiés, devis gratuits sous 48h.`}
        url={`https://www.afficheservices.fr/services/${category.slug}`}
      />
      <HowToSchema
        name={`Comment trouver un ${category.nameFr.toLowerCase()} de confiance`}
        steps={[
          { title: 'Décrivez votre projet', detail: 'Type de travaux, localisation, contraintes' },
          { title: 'Recevez jusqu\'à 3 devis', detail: 'Artisans vérifiés vous contactent sous 48h' },
          { title: 'Comparez et choisissez', detail: 'Prix, avis, expérience — zéro engagement' },
        ]}
      />
      {aggregateRating && aggregateRating.totalReviews > 0 && (
        <StructuredData data={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: `${category.nameFr} — AfficheServices`,
          url: `https://www.afficheservices.fr/services/${category.slug}`,
          provider: { '@type': 'Organization', name: 'AfficheServices' },
          areaServed: { '@type': 'Country', name: 'France' },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: aggregateRating.avgRating,
            reviewCount: aggregateRating.totalReviews,
            bestRating: 5,
            worstRating: 1,
          },
        }} />
      )}
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Services', url: 'https://www.afficheservices.fr/services' },
        { name: category.nameFr, url: `https://www.afficheservices.fr/services/${category.slug}` },
      ]} />

      {/* ── Breadcrumb ────────────────────────────────── */}
      <div className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-4 py-2.5 lg:px-6">
          <nav className="flex items-center gap-2 text-xs text-slate-500">
            <Link to="/" className="hover:text-primary">Accueil</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-primary">Services</Link>
            <span>/</span>
            <span className="text-slate-900">{category.nameFr}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
        {/* ── Header ──────────────────────────────────── */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <Icon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{category.nameFr}</h1>
                {/* Hero subtitle: hand-authored per category if available, else fall back to DB descriptionFr */}
                {(() => {
                  const seo = CATEGORY_SEO[category.slug]
                  if (seo?.heroSubtitle) {
                    return <p className="mt-2 text-sm leading-relaxed text-slate-600 max-w-3xl">{seo.heroSubtitle}</p>
                  }
                  if (category.descriptionFr) {
                    return <p className="mt-1 text-sm text-slate-500">{category.descriptionFr}</p>
                  }
                  return null
                })()}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-500">
              <span>
                <strong className="text-slate-900">{pagination.total}</strong> professionnel{pagination.total > 1 ? 's' : ''}
              </span>
              {category.leadPriceMin && category.leadPriceMax && (
                <span>
                  Prix moyen : <strong className="text-slate-900">{Math.round(Number(category.leadPriceMin))}–{Math.round(Number(category.leadPriceMax))} €</strong>
                </span>
              )}
              {isPending && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            </div>
          </div>
          <Link
            to="/demande-devis"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press sm:inline-flex sm:w-auto"
          >
            Devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* AI citation block — only when descriptionFr exists in DB. Without
            it, the synthesized passage would be identical boilerplate across
            all 30+ categories — duplicate content risk per Google's Scaled
            Content Abuse policy. Backfill descriptionFr to unlock per-category
            answer blocks. */}
        {category.descriptionFr && category.descriptionFr.length > 100 && (
          <AnswerBlock
            question={`Combien coûte un service de ${category.nameFr.toLowerCase()} en France en ${new Date().getFullYear()} ?`}
            parts={[
              category.descriptionFr,
              category.leadPriceMin && category.leadPriceMax
                ? `Les tarifs constatés varient en moyenne de ${category.leadPriceMin} € à ${category.leadPriceMax} € selon la nature de l'intervention, la complexité du chantier, les matériaux et la localisation. Les zones urbaines denses pratiquent des prix supérieurs de 15 à 25 % à la moyenne nationale.`
                : null,
              pagination.total > 0
                ? `${pagination.total} professionnels de ${category.nameFr.toLowerCase()} sont référencés sur AfficheServices avec contrôle d'identité, vérification SIRET et assurance RC Pro.`
                : null,
            ]}
          />
        )}

        {/* ── Hub-spoke internal linking (SEO) ──────────────── */}
        {(() => {
          const artisan = artisanSlug(category.slug)
          const artisanLabel = SERVICE_LABELS[category.slug]?.artisan ?? category.slug
          const isEmergency = EMERGENCY_SERVICES.includes(category.slug)
          return (
            <div className="mt-6 space-y-4">
              {/* Prix par ville — top 12 cities × current category */}
              <div className="rounded-2xl border border-border bg-white p-5 shadow-xs">
                <h2 className="text-sm font-bold text-slate-900">Prix {artisanLabel} par ville</h2>
                <p className="mt-1 text-xs text-slate-500">Tarifs détaillés dans les principales villes de France</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {TOP_CITIES.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/prix/${artisan}-${c.slug}`}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:border-primary hover:bg-primary-50 hover:text-primary"
                    >
                      Prix {artisanLabel} {c.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Urgence — emergency services only */}
              {isEmergency && (
                <div className="rounded-2xl border border-red-200 bg-red-50/40 p-5">
                  <h2 className="text-sm font-bold text-red-900">Urgence {artisanLabel} 24/7</h2>
                  <p className="mt-1 text-xs text-red-700">Intervention rapide à toute heure</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {TOP_CITIES.slice(0, 8).map((c) => (
                      <Link
                        key={c.slug}
                        to={`/urgence/${artisan}-${c.slug}`}
                        className="rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-100"
                      >
                        Urgence {artisanLabel} {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* National authority hubs — silo strength */}
              <div className="grid gap-3 sm:grid-cols-3">
                <Link to={`/tarif/${artisan}`} className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary hover:bg-primary-50">
                  <p className="text-xs font-semibold text-slate-500">Référence nationale</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">Tarifs {artisanLabel} France</p>
                </Link>
                <Link to={`/metier/${category.slug}`} className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary hover:bg-primary-50">
                  <p className="text-xs font-semibold text-slate-500">Le métier</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">Métier {category.nameFr.toLowerCase()}</p>
                </Link>
                <Link to="/rge/qualifications" className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 transition-colors hover:bg-emerald-50">
                  <p className="text-xs font-semibold text-emerald-700">Certifications</p>
                  <p className="mt-1 text-sm font-bold text-emerald-900">Qualifications RGE</p>
                </Link>
              </div>
            </div>
          )
        })()}

        {/* ── Subcategories ───────────────────────────── */}
        {subcategories.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {subcategories.map((sub) => (
              <Link
                key={sub.id}
                to={`/services/${sub.slug}`}
                className="rounded-full border border-border bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-xs transition-colors hover:border-primary hover:text-primary"
              >
                {sub.name}
              </Link>
            ))}
          </div>
        )}

        {/* ── Sort bar + Mobile filter toggle ─────────── */}
        <div className="mt-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">Trier :</span>
            <div className="flex flex-wrap gap-1.5">
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateFilter('sort', opt.value)}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
                    filters.sort === opt.value
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 lg:hidden"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filtres
            {activeFilterCount > 0 && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* ── Active filter chips ─────────────────────── */}
        {activeFilterCount > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {filters.rating && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                Note {filters.rating}+
                <button type="button" onClick={() => updateFilter('rating', '')} className="rounded-full p-0.5 hover:bg-primary/20">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.city && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                {filters.city}
                <button type="button" onClick={() => updateFilter('city', '')} className="rounded-full p-0.5 hover:bg-primary/20">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.experience && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                {filters.experience}+ ans
                <button type="button" onClick={() => updateFilter('experience', '')} className="rounded-full p-0.5 hover:bg-primary/20">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                Vérifié
                <button type="button" onClick={() => updateFilter('verified', '')} className="rounded-full p-0.5 hover:bg-primary/20">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        )}

        {/* ── Main layout ─────────────────────────────── */}
        <div className="mt-6 flex gap-6">
          {/* Desktop sidebar */}
          <aside className="hidden w-60 shrink-0 lg:block">
            <div className="sticky top-20 space-y-4">
              <div className="rounded-2xl border border-border bg-white p-5 shadow-xs">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <SlidersHorizontal className="h-4 w-4 text-slate-400" />
                  Filtres
                </h3>
                <FilterControls className="mt-4" />
              </div>
              <InstantQuote categorySlug={category.slug} categoryName={category.nameFr} />
            </div>
          </aside>

          {/* Mobile filter sheet */}
          {showMobileFilters && (
            <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Filtres">
              <button
                type="button"
                aria-label="Fermer les filtres"
                onClick={() => setShowMobileFilters(false)}
                className="absolute inset-0 cursor-default bg-black/40"
              />
              <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white p-6 animate-slide-in">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">Filtres</h3>
                  <button type="button" onClick={() => setShowMobileFilters(false)} className="rounded-full p-2 hover:bg-slate-100">
                    <X className="h-5 w-5 text-slate-500" />
                  </button>
                </div>
                <FilterControls className="mt-4" />
                <button
                  type="button"
                  onClick={() => setShowMobileFilters(false)}
                  className="mt-6 w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white hover:bg-primary-hover btn-press"
                >
                  Voir {pagination.total} résultat{pagination.total > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          <div className="min-w-0 flex-1">
            {/* Promoted pros */}
            {promotedPros.length > 0 && (
              <div className="mb-4 space-y-3">
                {promotedPros.map((pro) => (
                  <Link
                    key={pro.id}
                    to={`/pro/${pro.slug}`}
                    className="card-hover block rounded-2xl border-2 border-amber-200 bg-amber-50/50 p-5 shadow-xs sm:p-6"
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-4">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-lg font-bold text-amber-700">
                          {pro.companyName[0]}
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-slate-900">{pro.companyName}</h3>
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                              ⭐ Sponsorisé
                            </span>
                            {pro.identityVerified && (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-primary">
                                <CheckCircle className="h-3 w-3" /> Vérifié
                              </span>
                            )}
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm">
                            <div className="flex items-center gap-1">
                              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                              <span className="font-semibold text-slate-900">{formatRating(pro.ratingAvg)}</span>
                              <span className="text-slate-400">({pro.reviewCount})</span>
                            </div>
                            <span className="text-slate-200">|</span>
                            <span className="flex items-center gap-1 text-slate-500">
                              <MapPin className="h-3.5 w-3.5" /> {pro.city}
                            </span>
                          </div>
                          {pro.shortDescription && (
                            <p className="mt-2 line-clamp-2 text-sm text-slate-500">{pro.shortDescription}</p>
                          )}
                        </div>
                      </div>
                      <span className="shrink-0 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm">
                        Voir le profil <ArrowRight className="ml-1 inline h-3.5 w-3.5" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Map */}
            {pros.length > 0 && (
              <ProMap
                pros={pros}
                centerLat={parseFloat(pros[0]?.latitude ?? '48.8566')}
                centerLng={parseFloat(pros[0]?.longitude ?? '2.3522')}
                className="mb-6"
              />
            )}

            {pros.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-border bg-white p-12 text-center">
                <Icon className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-4 text-lg font-semibold text-slate-900">Aucun professionnel disponible</p>
                <p className="mt-2 text-sm text-slate-500">
                  {activeFilterCount > 0
                    ? 'Essayez de modifier vos filtres'
                    : 'Soyez le premier à vous inscrire dans cette catégorie'}
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  {activeFilterCount > 0 && (
                    <button type="button" onClick={clearAllFilters} className="rounded-xl border border-border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      Effacer les filtres
                    </button>
                  )}
                  <Link to="/register-pro" className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover btn-press">
                    Inscription Pro gratuite <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ) : (
              <div className={`space-y-3 ${isPending ? 'opacity-60 transition-opacity' : ''}`}>
                {pros.map((pro) => (
                  <Link
                    key={pro.id}
                    to={`/pro/${pro.slug}`}
                    className="card-hover block rounded-2xl border border-border bg-white p-5 shadow-xs sm:p-6"
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-4">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-lg font-bold text-primary">
                          {pro.companyName[0]}
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-slate-900">{pro.companyName}</h3>
                            {pro.identityVerified && (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-primary">
                                <CheckCircle className="h-3 w-3" /> Vérifié
                              </span>
                            )}
                            {pro.insuranceVerified && (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-600">
                                <Shield className="h-3 w-3" /> Assuré
                              </span>
                            )}
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm">
                            <div className="flex items-center gap-1">
                              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                              <span className="font-semibold text-slate-900">{formatRating(pro.ratingAvg)}</span>
                              <span className="text-slate-400">({pro.reviewCount ?? 0})</span>
                            </div>
                            <span className="text-slate-200">|</span>
                            <div className="flex items-center gap-1 text-slate-500">
                              <MapPin className="h-3.5 w-3.5" />
                              {pro.city} {pro.departmentCode && `(${pro.departmentCode})`}
                            </div>
                            {pro.responseTimeAvgHours && (
                              <>
                                <span className="text-slate-200">|</span>
                                <div className="flex items-center gap-1 text-slate-500">
                                  <Clock className="h-3.5 w-3.5" /> ~{pro.responseTimeAvgHours}h
                                </div>
                              </>
                            )}
                            {pro.yearsExperience && (
                              <>
                                <span className="text-slate-200">|</span>
                                <span className="text-slate-500">{pro.yearsExperience} ans exp.</span>
                              </>
                            )}
                            {pro.employeeCount && (
                              <>
                                <span className="text-slate-200">|</span>
                                <div className="flex items-center gap-1 text-slate-500">
                                  <Users className="h-3.5 w-3.5" /> {pro.employeeCount}
                                </div>
                              </>
                            )}
                          </div>
                          {pro.shortDescription && (
                            <p className="mt-2 line-clamp-2 text-sm text-slate-500">{pro.shortDescription}</p>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 sm:text-right">
                        <span className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press">
                          Voir le profil <ArrowRight className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* ── Pagination ──────────────────────────── */}
            {pagination.totalPages > 1 && (
              <nav className="mt-8 flex items-center justify-center gap-2" aria-label="Pagination">
                <button
                  type="button"
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-slate-500 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {Array.from({ length: Math.min(pagination.totalPages, 7) }, (_, i) => {
                  let pageNum: number
                  if (pagination.totalPages <= 7) {
                    pageNum = i + 1
                  } else if (pagination.page <= 4) {
                    pageNum = i + 1
                  } else if (pagination.page >= pagination.totalPages - 3) {
                    pageNum = pagination.totalPages - 6 + i
                  } else {
                    pageNum = pagination.page - 3 + i
                  }

                  return (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => goToPage(pageNum)}
                      className={`flex h-9 min-w-[36px] items-center justify-center rounded-lg border text-sm font-medium transition-all ${
                        pageNum === pagination.page
                          ? 'border-primary bg-primary text-white shadow-sm'
                          : 'border-border bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      aria-current={pageNum === pagination.page ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  )
                })}

                <button
                  type="button"
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-slate-500 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </nav>
            )}
          </div>
        </div>

        {/* ── Comparison Table ──────────────────────── */}
        <div className="mt-8">
          <ComparisonTable serviceName={category.nameFr} />
        </div>

        {/* ── Inline Quiz ─────────────────────────────── */}
        <div className="mt-8">
          <InlineQuiz serviceName={category.nameFr} cityName="votre ville" categorySlug={category.slug} />
        </div>

        {/* ── Related Services (internal linking) ────── */}
        {relatedCategories.length > 0 && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-5 shadow-xs">
            <h3 className="text-sm font-semibold text-slate-900">Vous pourriez aussi chercher</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {relatedCategories.map((rc) => (
                <Link
                  key={rc.slug}
                  to={`/services/${rc.slug}`}
                  className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-primary hover:bg-primary-50 hover:text-primary"
                >
                  {rc.name}
                </Link>
              ))}
              <Link
                to="/calculateur"
                className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100"
              >
                Calculateur de prix
              </Link>
            </div>
          </div>
        )}

        {/* ── City links (internal linking for SEO) ──── */}
        {cities.length > 0 && (
          <div className="mt-6 rounded-2xl border border-border bg-white p-5 shadow-xs">
            <h3 className="text-sm font-semibold text-slate-900">{category.nameFr} par ville</h3>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {cities.slice(0, 20).map((c) => {
                const citySlug = c.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
                return (
                  <Link
                    key={c}
                    to={`/services/${category.slug}/${citySlug}`}
                    className="rounded-full border border-slate-100 px-2.5 py-1 text-[11px] text-slate-500 transition-colors hover:border-primary hover:text-primary"
                  >
                    {category.nameFr} {c}
                  </Link>
                )
              })}
              {cities.length > 20 && (
                <span className="px-2 py-1 text-[11px] text-slate-400">+{cities.length - 20} villes</span>
              )}
            </div>
          </div>
        )}

        {/* ── FAQ Section ─────────────────────────────── */}
        {(() => {
          const seo = CATEGORY_SEO[category.slug]
          const merged = [
            ...(seo?.faqs.map(f => ({ question: f.q, answer: f.a })) ?? []),
            ...faqs,
          ]
          return (
            <div className="mt-10 rounded-2xl border border-border bg-white p-6 shadow-xs">
              <h2 className="text-lg font-bold text-slate-900">
                Questions fréquentes — {category.nameFr}
              </h2>
              <div className="mt-4 space-y-4">
                {merged.map((faq, i) => (
                  <details key={i} className="group border-b border-border pb-4 last:border-0 last:pb-0">
                    <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-800 hover:text-primary">
                      {faq.question}
                      <span className="shrink-0 text-slate-300 transition-transform group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          )
        })()}

        {/* ── Bottom CTA ──────────────────────────────── */}
        <div className="mt-12 rounded-2xl bg-surface-dark p-8 text-center sm:p-12">
          <h2 className="text-2xl font-bold text-white">
            Recevez 3 devis gratuits en {category.nameFr.toLowerCase()}
          </h2>
          <p className="mt-2 text-slate-400">Service gratuit, sans engagement, réponse sous 48h</p>
          <Link to="/demande-devis" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3.5 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press">
            Demander un devis <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  )
}
